/**
 * Fetches conversation history from the AG-UI GET endpoint and applies it
 * to the assistant-ui runtime via `runtime.thread.reset()`.
 *
 * The backend returns a JSON array of AG-UI Message objects (UserMessage,
 * AssistantMessage, ToolMessage). We convert them into the ThreadMessageLike
 * format that assistant-ui expects.
 *
 * Key structural mappings:
 * - During streaming, the AG-UI runtime builds ONE assistant ThreadMessage per
 *   user turn with all tool-call parts merged in. The backend history returns
 *   SEPARATE AssistantMessages per model response. We merge them here.
 * - The backend pre-resolves `final_result` artifacts server-side: the
 *   AssistantMessage carries `content` (answer text) plus `dataframes`,
 *   `figures`, `values` fields. We synthesize a `final_result` tool-call part
 *   from these so FinalResultMessage renders identically to the streaming case.
 */
import { useEffect, useState } from "react";
import type { AssistantRuntime, ThreadMessageLike } from "@assistant-ui/react";
import type { AssistantMessageCustomMetadata, MessageFeedback } from "~/components/assistant/types";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

interface AguiToolCall {
  id: string;
  type: string;
  function: { name: string; arguments: string };
}

interface AguiMessage {
  role: "user" | "assistant" | "tool";
  content: string | null;
  /** Message UUID from database (present on all messages from history). */
  id?: string;
  toolCalls?: AguiToolCall[];
  toolCallId?: string;
  dataframes?: unknown[];
  figures?: unknown[];
  values?: unknown[];
  text_files?: unknown[];
  blob_files?: unknown[];
  /** True when this message represents a cancelled agent run */
  cancelled?: boolean;
  /** Existing feedback on this user message (from history). */
  messageFeedback?: MessageFeedback | null;
}

function hasFinalResultArtifacts(msg: AguiMessage): boolean {
  return (
    (msg.dataframes != null && msg.dataframes.length > 0) ||
    (msg.figures != null && msg.figures.length > 0) ||
    (msg.values != null && msg.values.length > 0) ||
    (msg.text_files != null && msg.text_files.length > 0) ||
    (msg.blob_files != null && msg.blob_files.length > 0)
  );
}

/**
 * Build an array of pre-resolved enriched artifacts from the server-resolved
 * extra fields on the AssistantMessage. The shape matches EnrichedArtifact in
 * FinalResultMessage.tsx.
 */
function buildResolvedArtifacts(msg: AguiMessage): unknown[] {
  const artifacts: unknown[] = [];

  for (const df of msg.dataframes ?? []) {
    artifacts.push({ ...(df as Record<string, unknown>), type: "dataframe" });
  }
  for (const fig of msg.figures ?? []) {
    artifacts.push({ ...(fig as Record<string, unknown>), type: "figure" });
  }
  for (const val of msg.values ?? []) {
    artifacts.push({ ...(val as Record<string, unknown>), type: "value" });
  }
  for (const tf of msg.text_files ?? []) {
    artifacts.push({ ...(tf as Record<string, unknown>), type: "text_file" });
  }
  for (const bf of msg.blob_files ?? []) {
    artifacts.push({ ...(bf as Record<string, unknown>), type: "blob_file" });
  }

  return artifacts;
}

type ContentPart =
  | { readonly type: "text"; readonly text: string }
  | {
      readonly type: "tool-call";
      readonly toolCallId: string;
      readonly toolName: string;
      readonly args: Record<string, unknown>;
      readonly argsText: string;
      result?: string | null;
    };

/**
 * Converts AG-UI messages into ThreadMessageLike[], merging consecutive
 * assistant + tool messages into a single assistant message per user turn.
 *
 * For the final-result AssistantMessage (which has content + pre-resolved
 * artifact fields), we synthesize a `final_result` tool-call part so that
 * FinalResultMessage renders it identically to the streaming case.
 */
function convertAguiToThreadMessages(aguiMessages: AguiMessage[]): ThreadMessageLike[] {
  const result: ThreadMessageLike[] = [];
  let currentParts: ContentPart[] = [];
  let finalResultCounter = 0;
  // Track the last user message's backend ID + feedback so we can attach
  // them to the assistant message that follows (where the action bar lives).
  let lastUserBackendId: string | undefined;
  let lastUserFeedback: AguiMessage["messageFeedback"] | undefined;
  // Set when a cancelled AssistantMessage is encountered, so the flushed
  // assistant message carries the cancelled flag in its metadata.
  let pendingCancelled = false;

  const flushAssistant = () => {
    if (currentParts.length > 0 || pendingCancelled) {
      const custom: AssistantMessageCustomMetadata = {};
      if (lastUserBackendId) {
        custom.backendMessageId = lastUserBackendId;
      }
      if (lastUserFeedback) {
        custom.feedback = lastUserFeedback;
      }
      if (pendingCancelled) {
        custom.cancelled = true;
      }
      result.push({
        role: "assistant",
        content: currentParts as ThreadMessageLike["content"],
        metadata: Object.keys(custom).length > 0 ? { custom } : undefined,
      });
      currentParts = [];
      pendingCancelled = false;
    }
  };

  for (const msg of aguiMessages) {
    if (msg.role === "user") {
      flushAssistant();
      lastUserBackendId = msg.id;
      lastUserFeedback = msg.messageFeedback;
      result.push({
        role: "user",
        content: msg.content ?? "",
      });
    } else if (msg.role === "assistant") {
      // Cancelled message — flag it so the flushed assistant message
      // carries cancelled metadata. The AssistantMessage component
      // renders <CancellationMessage /> when it sees the flag.
      if (msg.cancelled) {
        pendingCancelled = true;
        continue;
      }

      // When an assistant message has both text content and tool calls,
      // the text is "thinking" narration (e.g. "Step 1: Retrieve the file").
      // V1 only showed this transiently during streaming — omit from history.

      // Standard tool calls (data_retriever, python_analyzer, etc.)
      if (msg.toolCalls) {
        for (const tc of msg.toolCalls) {
          let args: Record<string, unknown> = {};
          try {
            args = JSON.parse(tc.function.arguments);
          } catch {
            // keep empty args if parse fails
          }

          currentParts.push({
            type: "tool-call",
            toolCallId: tc.id,
            toolName: tc.function.name,
            args,
            argsText: tc.function.arguments,
          });
        }
      }

      if (hasFinalResultArtifacts(msg) || (msg.content && !msg.toolCalls)) {
        // This is the final-result message: backend extracted `final_result`
        // content into `msg.content` and resolved artifacts into extra fields.
        // Synthesize a `final_result` tool-call part so it renders via
        // FinalResultMessage exactly as during streaming.
        const resolvedArtifacts = buildResolvedArtifacts(msg);
        const finalArgs: Record<string, unknown> = {
          content: msg.content ?? "",
          artifacts: [],
          _resolvedArtifacts: resolvedArtifacts,
        };
        currentParts.push({
          type: "tool-call",
          toolCallId: `final-result-history-${finalResultCounter++}`,
          toolName: "final_result",
          args: finalArgs,
          argsText: "",
          result: "ok",
        });
      }
    } else if (msg.role === "tool") {
      // Find the matching tool-call part and attach the result.
      // Search in reverse so we match the most recent tool call.
      for (let i = currentParts.length - 1; i >= 0; i--) {
        const part = currentParts[i];
        if (part.type === "tool-call" && part.toolCallId === msg.toolCallId) {
          part.result = msg.content;
          break;
        }
      }
    }
  }

  flushAssistant();
  return result;
}

export async function fetchConversationHistory(
  conversationId: string
): Promise<ThreadMessageLike[]> {
  const response = await fetch(
    `${VITE_ANALYSIS_AGENT_API}/conversations/${conversationId}/messages_agui`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch conversation history: ${response.status}`);
  }

  const rawMessages: AguiMessage[] = await response.json();

  if (!rawMessages.length) {
    return [];
  }

  return convertAguiToThreadMessages(rawMessages);
}

/**
 * Loads conversation history from the AG-UI GET endpoint when the conversation
 * changes, and applies it to the runtime via `reset()`.
 *
 * User messages in the loaded history carry `backendMessageId` and `feedback`
 * in their metadata, so downstream components (e.g. the feedback action bar)
 * can read them directly from the thread without extra plumbing.
 */
export function useConversationHistory(runtime: AssistantRuntime, conversationId: string | null) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!conversationId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    runtime.thread.reset();

    let cancelled = false;

    (async () => {
      try {
        const messages = await fetchConversationHistory(conversationId);
        if (cancelled) {
          return;
        }
        if (messages.length > 0) {
          runtime.thread.reset(messages);
        }
      } catch (error) {
        console.error("Failed to load conversation history:", error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [conversationId, runtime]);

  return { isLoadingHistory: isLoading };
}
